import React, { useState, useEffect } from 'react';
import { Button, Space, Modal, Form, Input, Card, Tree, message } from 'antd';
import {
  PlusOutlined,
  TeamOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { DataNode } from 'antd/es/tree';
import { Service } from '@/apis';
import type {
  ManagerRoleBaseResData,
  ManagerRoleSaveDto,
  ManagerRolePutDto,
  ManagerPermissionBaseResData,
} from '@/apis';
import {
  DataStats,
  DataTable,
  type DataStat,
  type ActionButton,
} from '@/components/CommonTable';
import { useDataTableWithSearch } from '@/hooks/useDataTableWithSearch';
import { useFormModal } from '@/hooks/useFormModal';
import { SearchBar } from '@/components/SearchBar';
import {
  createTimeColumn,
  updateTimeColumn,
  createLongTextColumn,
  createPermissionKeysColumn,
} from '@/utils/commonUtils';
import styles from '@/components/CommonTable/index.module.less';

// 使用API返回的数据类型
type RoleData = ManagerRoleBaseResData;

// 表单中使用的角色数据类型（权限标识为数组）
type RoleFormData = Omit<RoleData, 'permissionKeys'> & {
  permissionKeys: string[];
};

const RoleManagement: React.FC = () => {
  const [permissionModalVisible, setPermissionModalVisible] = useState(false);
  const [selectedRolePermissions, setSelectedRolePermissions] = useState<
    string[]
  >([]);
  const [currentRoleForPermission, setCurrentRoleForPermission] =
    useState<RoleData | null>(null);
  const [permissionSearchValue, setPermissionSearchValue] = useState('');
  const [permissionSelectModalVisible, setPermissionSelectModalVisible] =
    useState(false);
  const [selectedPermissionsForForm, setSelectedPermissionsForForm] = useState<
    string[]
  >([]);

  // 权限数据状态
  const [permissionOptions, setPermissionOptions] = useState<
    ManagerPermissionBaseResData[]
  >([]);
  const [permissionLoading, setPermissionLoading] = useState(false);

  // 获取权限选项数据
  const fetchPermissionOptions = async () => {
    setPermissionLoading(true);
    try {
      const response = await Service.managerPermissionControllerGet(1, 1000); // 获取所有权限
      const permissions = response.data?.rows || [];
      console.log('获取到的权限数据:', permissions);
      setPermissionOptions(permissions);
    } catch (error) {
      console.error('获取权限选项失败:', error);
      message.error('获取权限选项失败');
    } finally {
      setPermissionLoading(false);
    }
  };

  // 组件加载时获取权限选项
  useEffect(() => {
    fetchPermissionOptions();
  }, []);

  // 临时测试数据 - 如果API数据为空时使用
  const getTestPermissions = (): ManagerPermissionBaseResData[] => [
    {
      permissionId: 1,
      permissionKey: 'system',
      parentKey: '',
      permissionName: '系统管理',
      permissionType: 1,
      permissionMark: '/system',
      permissionMethod: 'GET',
      icon: 'setting',
      createUid: 'admin',
      updateUid: 'admin',
      createTime: Date.now(),
      updateTime: Date.now(),
    },
    {
      permissionId: 2,
      permissionKey: 'system:user',
      parentKey: 'system',
      permissionName: '用户管理',
      permissionType: 1,
      permissionMark: '/system/user',
      permissionMethod: 'GET',
      icon: 'user',
      createUid: 'admin',
      updateUid: 'admin',
      createTime: Date.now(),
      updateTime: Date.now(),
    },
    {
      permissionId: 3,
      permissionKey: 'system:role',
      parentKey: 'system',
      permissionName: '角色管理',
      permissionType: 1,
      permissionMark: '/system/role',
      permissionMethod: 'GET',
      icon: 'team',
      createUid: 'admin',
      updateUid: 'admin',
      createTime: Date.now(),
      updateTime: Date.now(),
    },
    {
      permissionId: 4,
      permissionKey: 'system:permission',
      parentKey: 'system',
      permissionName: '权限管理',
      permissionType: 1,
      permissionMark: '/system/permission',
      permissionMethod: 'GET',
      icon: 'safety',
      createUid: 'admin',
      updateUid: 'admin',
      createTime: Date.now(),
      updateTime: Date.now(),
    },
    {
      permissionId: 5,
      permissionKey: 'content',
      parentKey: '',
      permissionName: '内容管理',
      permissionType: 1,
      permissionMark: '/content',
      permissionMethod: 'GET',
      icon: 'file',
      createUid: 'admin',
      updateUid: 'admin',
      createTime: Date.now(),
      updateTime: Date.now(),
    },
  ];

  // 使用带搜索功能的表格管理Hook
  const {
    data: roles,
    loading,
    pagination,
    fetchData: fetchRoles,
    handleDelete,
    handlePaginationChange,
    search,
  } = useDataTableWithSearch<RoleData>({
    fetchData: Service.managerRoleControllerGet,
    deleteData: async (id: number) => {
      await Service.managerRoleControllerDelete(id);
    },
    errorMessage: '获取角色列表失败',
    searchOptions: {
      fields: ['roleName', 'permissionKeys'],
    },
  });

  // 使用Modal管理Hook
  const {
    visible,
    editingItem: editingRole,
    form,
    handleAdd,
    handleEdit,
    handleOk,
    handleCancel,
  } = useFormModal<RoleData>({
    createData: async (data: ManagerRoleSaveDto) => {
      // 转换权限数组为逗号分隔的字符串
      let permissionKeysValue = data.permissionKeys;
      if (Array.isArray(data.permissionKeys)) {
        // 如果是数组，直接join
        permissionKeysValue = data.permissionKeys.join(',');
      }

      // 只传递必要的字段
      const transformedData: ManagerRoleSaveDto = {
        roleName: data.roleName,
        permissionKeys: permissionKeysValue,
      };
      console.log('创建角色 - 保存数据:', transformedData);
      await Service.managerRoleControllerSave(transformedData);
    },
    updateData: async (data: ManagerRolePutDto) => {
      // 转换权限数组为逗号分隔的字符串
      let permissionKeysValue = data.permissionKeys;
      if (Array.isArray(data.permissionKeys)) {
        // 如果是数组，直接join
        permissionKeysValue = data.permissionKeys.join(',');
      }

      // 只更新必要的字段，避免上传所有字段
      const transformedData: ManagerRolePutDto = {
        roleId: data.roleId,
        roleName: data.roleName,
        permissionKeys: permissionKeysValue,
      };
      console.log('更新角色 - 保存数据:', transformedData);
      await Service.managerRoleControllerPut(transformedData);
    },
  });

  // 处理Modal确认后的数据刷新
  const handleModalOkWithRefresh = async () => {
    const success = await handleOk();
    if (success) {
      fetchRoles();
    }
  };

  // 处理编辑时的数据转换
  const handleEditWithTransform = (record: RoleData) => {
    // 处理permissionKeys字段，确保转换为数组格式
    let permissionKeysArray: string[] = [];
    if (record.permissionKeys) {
      if (typeof record.permissionKeys === 'string') {
        permissionKeysArray = record.permissionKeys
          .split(',')
          .filter(key => key.trim());
      } else if (Array.isArray(record.permissionKeys)) {
        permissionKeysArray = record.permissionKeys;
      }
    }

    console.log('编辑角色 - 原始权限数据:', permissionKeysArray);

    const transformedRecord: RoleFormData = {
      ...record,
      // 直接使用permissionKey数组
      permissionKeys: permissionKeysArray,
    };
    handleEdit(transformedRecord as unknown as RoleData);
  };

  const handlePermission = (record: RoleData) => {
    // 获取当前角色的权限
    let currentPermissions: string[] = [];
    if (record.permissionKeys) {
      if (typeof record.permissionKeys === 'string') {
        currentPermissions = record.permissionKeys
          .split(',')
          .filter(key => key.trim());
      } else if (Array.isArray(record.permissionKeys)) {
        currentPermissions = record.permissionKeys;
      }
    }

    console.log('设置角色权限 - 角色数据:', record);
    console.log('设置角色权限 - permissionKeys字段:', record.permissionKeys);
    console.log('设置角色权限 - 解析后的权限:', currentPermissions);
    console.log(
      '设置角色权限 - 权限选项:',
      permissionOptions.map(p => ({
        id: p.permissionId,
        key: p.permissionKey,
        name: p.permissionName,
      }))
    );

    setCurrentRoleForPermission(record);
    // 直接使用permissionKey集合
    setSelectedRolePermissions(currentPermissions);
    setPermissionModalVisible(true);
  };

  const handlePermissionOk = async () => {
    if (!currentRoleForPermission) {
      message.error('未找到当前角色信息');
      return;
    }

    try {
      // 直接以permissionKey保存
      const updatedRole: ManagerRolePutDto = {
        roleId: currentRoleForPermission.roleId,
        roleName: currentRoleForPermission.roleName,
        permissionKeys: selectedRolePermissions.join(','),
      };

      await Service.managerRoleControllerPut(updatedRole);
      message.success('权限设置成功');
      setPermissionModalVisible(false);
      setCurrentRoleForPermission(null);
      // 刷新角色列表
      fetchRoles();
    } catch (error) {
      console.error('权限设置失败:', error);
      message.error('权限设置失败');
    }
  };

  // 处理表单中的权限选择
  const handleFormPermissionSelect = () => {
    const currentPermissionsValue = form.getFieldValue('permissionKeys') || [];
    // 处理字符串格式的permissionKeys（逗号分隔）
    let currentPermissions: string[] = [];
    if (Array.isArray(currentPermissionsValue)) {
      currentPermissions = currentPermissionsValue;
    } else {
      currentPermissions = currentPermissionsValue
        .split(',')
        .filter((key: string) => key.trim());
    }

    console.log('表单权限选择 - 表单字段值:', currentPermissionsValue);
    console.log('表单权限选择 - 解析后权限:', currentPermissions);

    // 表单中直接使用permissionKey
    console.log('表单权限选择 - 最终权限Key:', currentPermissions);

    setSelectedPermissionsForForm(currentPermissions);
    setPermissionSelectModalVisible(true);
  };

  // 确认表单权限选择
  const handleFormPermissionOk = () => {
    // 直接以permissionKey保存到表单
    console.log('表单保存时的权限Key:', selectedPermissionsForForm);

    form.setFieldsValue({
      permissionKeys: selectedPermissionsForForm,
    });
    setPermissionSelectModalVisible(false);
  };

  // 不再进行ID与Key的相互转换，前后端统一使用permissionKey

  // 将权限数据转换为多层树形结构
  const convertPermissionsToTreeData = (
    permissions: ManagerPermissionBaseResData[]
  ): DataNode[] => {
    console.log('转换权限数据:', permissions);

    // 创建权限映射，便于查找
    const permissionMap = new Map<string, ManagerPermissionBaseResData>();
    permissions.forEach(permission => {
      permissionMap.set(permission.permissionKey, permission);
    });

    // 找到根节点（没有父级权限的权限）
    const rootPermissions = permissions.filter(
      permission =>
        !permission.parentKey ||
        permission.parentKey === '' ||
        permission.parentKey === '-' ||
        permission.parentKey === null ||
        permission.parentKey === undefined
    );

    console.log('根权限节点:', rootPermissions);
    console.log(
      '所有权限的parentKey:',
      permissions.map(p => ({
        key: p.permissionKey,
        name: p.permissionName,
        parentKey: p.parentKey,
        parentKeyType: typeof p.parentKey,
      }))
    );

    // 递归构建多层树形结构
    const buildTree = (
      parentPermissions: ManagerPermissionBaseResData[]
    ): DataNode[] => {
      return parentPermissions.map(permission => {
        // 找到所有子权限
        const children = permissions.filter(
          child => child.parentKey === permission.permissionKey
        );

        console.log(
          `权限 ${permission.permissionName} 的子权限:`,
          children.map(c => ({
            key: c.permissionKey,
            name: c.permissionName,
            parentKey: c.parentKey,
          }))
        );

        return {
          title: `${permission.permissionName} (${permission.permissionKey})`,
          key: permission.permissionKey,
          children: children.length > 0 ? buildTree(children) : undefined,
        };
      });
    };

    const treeData = buildTree(rootPermissions);
    console.log('转换后的树数据:', treeData);
    return treeData;
  };

  // 过滤权限树数据
  const filterTreeData = (
    treeData: DataNode[],
    searchValue: string
  ): DataNode[] => {
    if (!searchValue) return treeData;

    const filterNode = (node: DataNode): DataNode | null => {
      const title = node.title as string;
      const matches = title.toLowerCase().includes(searchValue.toLowerCase());

      let filteredChildren: DataNode[] = [];
      if (node.children) {
        filteredChildren = node.children
          .map(child => filterNode(child))
          .filter((child): child is DataNode => child !== null);
      }

      if (matches || filteredChildren.length > 0) {
        return {
          ...node,
          children:
            filteredChildren.length > 0 ? filteredChildren : node.children,
        };
      }

      return null;
    };

    return treeData
      .map(node => filterNode(node))
      .filter((node): node is DataNode => node !== null);
  };

  const columns: ColumnsType<RoleData> = [
    createLongTextColumn('角色名称', 'roleName', 10),
    createPermissionKeysColumn('权限标识', 'permissionKeys', 3),
    createTimeColumn('full'),
    updateTimeColumn('full'),
  ];

  // 统计卡片数据
  const stats: DataStat[] = [
    {
      title: '总角色数',
      value: pagination.total,
      prefix: <TeamOutlined />,
      valueStyle: { color: '#3f8600' },
    },
    {
      title: '当前页角色',
      value: roles.length,
      valueStyle: { color: '#1890ff' },
    },
    {
      title: '权限模块',
      value: permissionOptions.filter(p => !p.parentKey || p.parentKey === '')
        .length,
      valueStyle: { color: '#cf1322' },
    },
    {
      title: '系统角色',
      value: roles.filter(r => r.roleName.includes('管理员')).length,
      valueStyle: { color: '#722ed1' },
    },
  ];

  // 操作按钮配置
  const actionButtons: ActionButton<RoleData>[] = [
    {
      key: 'edit',
      label: '编辑',
      icon: <EditOutlined />,
      onClick: handleEditWithTransform,
    },
    {
      key: 'permission',
      label: '权限',
      icon: <SettingOutlined />,
      onClick: handlePermission,
    },
    {
      key: 'delete',
      label: '删除',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: record => handleDelete(record.roleId),
      confirm: {
        title: '确定要删除这个角色吗？',
        okText: '确定',
        cancelText: '取消',
      },
    },
  ];

  return (
    <div className={styles.dataTablePage}>
      <Card className={styles.card}>
        {/* 统计卡片 */}
        <DataStats stats={stats} className={styles.statsRow} />

        {/* 操作栏 */}
        <div className={styles.toolbar}>
          <Space wrap>
            <Button type='primary' icon={<PlusOutlined />} onClick={handleAdd}>
              新增角色
            </Button>

            {/* 搜索区域 */}
            <SearchBar
              search={search}
              placeholder='搜索角色名称或权限'
              width={300}
              loading={loading}
            />
          </Space>
        </div>

        {/* 角色表格 */}
        <DataTable
          columns={columns}
          dataSource={roles}
          loading={loading}
          rowKey='roleId'
          pagination={pagination}
          onPaginationChange={handlePaginationChange}
          actionButtons={actionButtons}
        />
      </Card>

      {/* 新增/编辑角色弹窗 */}
      <Modal
        title={editingRole ? '编辑角色' : '新增角色'}
        open={visible}
        onOk={handleModalOkWithRefresh}
        onCancel={handleCancel}
        width={600}
      >
        <Form form={form} layout='vertical'>
          <Form.Item
            name='roleName'
            label='角色名称'
            rules={[{ required: true, message: '请输入角色名称' }]}
          >
            <Input placeholder='请输入角色名称' />
          </Form.Item>
          <Form.Item
            name='permissionKeys'
            label='权限标识'
            rules={[{ required: true, message: '请选择权限标识' }]}
          >
            <Form.Item
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.permissionKeys !== currentValues.permissionKeys
              }
            >
              {({ getFieldValue }) => (
                <div>
                  <Button
                    type='default'
                    onClick={handleFormPermissionSelect}
                    loading={permissionLoading}
                  >
                    选择权限
                  </Button>
                  <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                    已选择 {getFieldValue('permissionKeys')?.length || 0} 个权限
                  </div>
                </div>
              )}
            </Form.Item>
          </Form.Item>
        </Form>
      </Modal>

      {/* 权限设置弹窗 */}
      <Modal
        title={`设置角色权限 - ${currentRoleForPermission?.roleName || '角色'}`}
        open={permissionModalVisible}
        onOk={handlePermissionOk}
        onCancel={() => {
          setPermissionModalVisible(false);
          setCurrentRoleForPermission(null);
          setPermissionSearchValue('');
        }}
        width={800}
        okText='确定'
        cancelText='取消'
      >
        <div style={{ marginBottom: 16 }}>
          <Input
            placeholder='搜索权限...'
            value={permissionSearchValue}
            onChange={e => setPermissionSearchValue(e.target.value)}
            allowClear
          />
        </div>
        <div style={{ marginBottom: 16, fontSize: 12, color: '#666' }}>
          权限数据: {permissionOptions.length} 条 | 树节点:{' '}
          {
            convertPermissionsToTreeData(
              permissionOptions.length > 0
                ? permissionOptions
                : getTestPermissions()
            ).length
          }{' '}
          个 | 已选中: {selectedRolePermissions.length} 个
        </div>
        <Tree
          checkable
          checkedKeys={selectedRolePermissions}
          onCheck={checkedKeys =>
            setSelectedRolePermissions(checkedKeys as string[])
          }
          treeData={filterTreeData(
            convertPermissionsToTreeData(
              permissionOptions.length > 0
                ? permissionOptions
                : getTestPermissions()
            ),
            permissionSearchValue
          )}
          defaultExpandAll
          showLine
          height={400}
        />
      </Modal>

      {/* 表单权限选择弹窗 */}
      <Modal
        title='选择权限'
        open={permissionSelectModalVisible}
        onOk={handleFormPermissionOk}
        onCancel={() => {
          setPermissionSelectModalVisible(false);
          setPermissionSearchValue('');
        }}
        width={800}
        okText='确定'
        cancelText='取消'
      >
        <div style={{ marginBottom: 16 }}>
          <Input
            placeholder='搜索权限...'
            value={permissionSearchValue}
            onChange={e => setPermissionSearchValue(e.target.value)}
            allowClear
          />
        </div>
        <Tree
          checkable
          checkedKeys={selectedPermissionsForForm}
          onCheck={checkedKeys =>
            setSelectedPermissionsForForm(checkedKeys as string[])
          }
          treeData={filterTreeData(
            convertPermissionsToTreeData(
              permissionOptions.length > 0
                ? permissionOptions
                : getTestPermissions()
            ),
            permissionSearchValue
          )}
          defaultExpandAll
          showLine
          height={400}
        />
      </Modal>
    </div>
  );
};

export default RoleManagement;
